import { defineStore } from 'pinia'
import api from '@/helpers/api'
import { workFilters, WorkStatuses } from '@/constants'
import dashboardApi from '@/helpers/dashboardApi'

export const filtersNames = Object.keys(workFilters)

interface WorksStore {
  works: Work[],
  acceptedWorksCount: number | null,
  worksCount: number | null,
  worksCountByFilters: number | null,
}

export const useWorksStore = defineStore('works', {
  state: () => ({
    works: [],
    acceptedWorksCount: null,
    worksCount: null,
    worksCountByFilters: null,
  }) as WorksStore,

  getters: {
    getWorkById: state => (id: number) => state.works.find(work => work.id === id),
  },

  actions: {
    formatWork(work: WorkForm): WorkForm {
      return {
        ...work,
        alternative_titles: work.alternative_titles.filter(({ name }) => name),
        writers: work.writers.map(writer => ({ ...writer, share: parseFloat(String(writer.share)) })),
        samples: work.samples.filter(({ writers_names, share, title }) => writers_names && share && title).length
          ? work.samples.map(sample => ({ ...sample, share: parseFloat(String(sample.share)) }))
          : [],
      }
    },

    async acceptShareForWriter(workId: number, writerId: number) {
      const { data: { workStatus } } = await api.post(`/publishing/writer/accept-share/${writerId}`)

      this.$patch(state => {
        const work = state.works.find(work => work.id === workId)
        const writer = work?.writers.find(writer => writer.id === writerId)

        if (writer && work) {
          writer.isControlled = false
          work.status = workStatus
        }
      })
    },

    async createWork(work: WorkForm) {
      const formattedWork = {
        ...this.formatWork(work),
        music_work_status_id: 1,
      }

      const response = await api.post('/publishing/works', { musicWork: formattedWork })

      this.$patch(state => {
        state.works = [...state.works, response.data]
      })

      return response
    },

    async editWork(workData: WorkForm): Promise<Work> {
      const response = await api.post<Work>(`/publishing/works/${workData.id}`, { musicWork: this.formatWork(workData) })

      this.$patch(state => {
        const workIndex = state.works.findIndex(work => work.id === workData.id)

        state.works[workIndex] = {
          ...response.data,
          title: state.works[workIndex].title,
        }
      })

      return response.data
    },

    async deleteWork(workId: number) {
      await api.delete(`/publishing/works/${workId}`)

      const workIndex = this.works.findIndex(work => work.id === workId)
      this.works.splice(workIndex, 1)
      this.worksCount && this.worksCount--
      this.worksCountByFilters && this.worksCountByFilters--
    },
    async getUserAcceptedWorksCount() {
      const { pagination: { total } } = await api.get<unknown, ApiPaginatedResponse<Work[]>>('/publishing/user/owner-works', {
        params: {
          status: [
            WorkStatuses.accepted.id,
            WorkStatuses.registered.id,
            WorkStatuses.updating.id,
            WorkStatuses.registering.id,
          ],
          per_page: 1,
        },
      })

      this.acceptedWorksCount = total
    },

    async getWorksCount() {
      const { pagination: { total } } = await api.get<unknown, ApiPaginatedResponse<Work[]>>('/publishing/user/owner-works', {
        params: {
          per_page: 1,
        },
      })

      this.worksCount = total
    },

    async getWorks(statuses: number[] = [], page: number | boolean = 1) {
      const { data, pagination } = await api.get<unknown, ApiPaginatedResponse<Work[]>>('/publishing/user/owner-works', {
        params: {
          page,
          pagination: (page === false) ? page : true,
          status: statuses,
        },
      })

      if (pagination) {
        if (pagination && !this.worksCount && !statuses.length) {
          this.worksCount = pagination.total
        }

        this.worksCountByFilters = pagination.total
      } else {
        this.worksCount = data.length
      }

      return data
    },

    async getUserWorksByStatuses(statuses: number[] = [], page: number | boolean = 1) {
      const works = await this.getWorks(statuses, page)

      if (works && works.length) {
        this.works = works
      }
    },

    setWriterEmailSent(workId: number, writerId: number) {
      const work = this.works.find(work => work.id === workId)
      const writer = work?.writers.find(writer => writer.id === writerId)

      if (writer) {
        writer.sent_emails = (writer.sent_emails || 0) + 1
      }
    },

    async sendInviteEmail(workId: number, writerId: number) {
      await api.post(`/publishing/works/${workId}/writers/${writerId}/send-invite-email`)

      this.setWriterEmailSent(workId, writerId)
    },

    async terminateWork(workId: number) {
      const { data } = await dashboardApi.post(`/api/me/music_works/${workId}/terminate`, { id: workId })
      this.$patch(state => {
        const index = state.works.findIndex(work => work.id === workId)
        if (index !== -1) {
          state.works[index] = {
            ...state.works[index],
            status: data.status,
            terminated_at: data.terminatedAt,
          }
        }
      })
    },
  },
})
